// ============================================================================
// StaffApiService — typed wrapper over TicketingClient for
// ticketing.v1.StaffService. Returns wire shapes; tenant_id is injected by the
// client. The whole service requires backend auth (see staff.types.ts) — callers
// must handle a 404/ConnectError when running against an open-mode backend.
// ============================================================================
import { ticketingClient } from '../ticketing/ticketing.client';
import type {
  CreateRoleBody,
  InviteStaffBody,
  ListRolesResponse,
  ListStaffResponse,
  UpdateRoleBody,
  WhoAmIResponse,
  WireRole,
  WireStaffMember,
  WireStaffStatus,
} from './staff.types';

const SERVICE = 'ticketing.v1.StaffService';

export class StaffApiService {
  private readonly client = ticketingClient;

  /** Caller identity + effective permissions + ticket scope (no permission required). */
  whoAmI(signal?: AbortSignal): Promise<WhoAmIResponse> {
    return this.client.unary<WhoAmIResponse>(SERVICE, 'WhoAmI', {}, { signal });
  }

  /** The tenant's staff roster (needs staff.manage or staff.invite). */
  async listStaff(signal?: AbortSignal): Promise<WireStaffMember[]> {
    const res = await this.client.unary<ListStaffResponse>(SERVICE, 'ListStaff', {}, { signal });
    return res.staff ?? [];
  }

  /** The tenant's role definitions (needs staff.invite/manage or roles.manage). */
  async listRoles(signal?: AbortSignal): Promise<WireRole[]> {
    const res = await this.client.unary<ListRolesResponse>(SERVICE, 'ListRoles', {}, { signal });
    return res.roles ?? [];
  }

  /** The roles granted to one staff member (needs staff.manage/invite). */
  async listStaffRoles(staffId: string, signal?: AbortSignal): Promise<WireRole[]> {
    const res = await this.client.unary<ListRolesResponse>(SERVICE, 'ListStaffRoles', { staff_id: staffId }, { signal });
    return res.roles ?? [];
  }

  /** Define a new custom role (needs roles.manage; rank-bounded, backend-enforced). */
  createRole(body: CreateRoleBody, signal?: AbortSignal): Promise<WireRole> {
    return this.client.unary<WireRole>(SERVICE, 'CreateRole', { ...body }, { signal });
  }

  /** Edit a role's name/permissions (and rank for custom roles). */
  updateRole(body: UpdateRoleBody, signal?: AbortSignal): Promise<WireRole> {
    return this.client.unary<WireRole>(SERVICE, 'UpdateRole', { ...body }, { signal });
  }

  /** Delete a custom role (built-ins and still-assigned roles are refused). */
  deleteRole(id: string, signal?: AbortSignal): Promise<void> {
    return this.client.unary<void>(SERVICE, 'DeleteRole', { id }, { signal });
  }

  /** Invite a new staff member with optional initial roles. */
  invite(body: InviteStaffBody, signal?: AbortSignal): Promise<WireStaffMember> {
    return this.client.unary<WireStaffMember>(SERVICE, 'InviteStaff', { ...body }, { signal });
  }

  /** Grant a role (rank-bounded by the actor's own rank, backend-enforced). */
  grantRole(staffId: string, roleKey: string, signal?: AbortSignal): Promise<WireStaffMember> {
    return this.client.unary<WireStaffMember>(SERVICE, 'GrantRole', { staff_id: staffId, role_key: roleKey }, { signal });
  }

  /** Revoke a role. */
  revokeRole(staffId: string, roleKey: string, signal?: AbortSignal): Promise<WireStaffMember> {
    return this.client.unary<WireStaffMember>(SERVICE, 'RevokeRole', { staff_id: staffId, role_key: roleKey }, { signal });
  }

  /** Activate / suspend a member (cannot self-target, backend-enforced). */
  setStatus(staffId: string, status: WireStaffStatus, signal?: AbortSignal): Promise<WireStaffMember> {
    return this.client.unary<WireStaffMember>(SERVICE, 'SetStaffStatus', { staff_id: staffId, status }, { signal });
  }

  /** Suspend a member AND dispose of their open tickets: unassign (default) or
   *  transfer to an active member. Returns the count moved. (Reactivation stays on
   *  setStatus.) */
  suspendStaff(staffId: string, disposition: 'unassign' | 'transfer', transferToStaffId?: string, signal?: AbortSignal): Promise<{ staff?: WireStaffMember; ticketsMoved?: number }> {
    return this.client.unary<{ staff?: WireStaffMember; ticketsMoved?: number }>(
      SERVICE,
      'SuspendStaff',
      { staff_id: staffId, disposition, transfer_to_staff_id: transferToStaffId ?? '' },
      { signal },
    );
  }

  /** Email a managed-login member a set-password link. Managed login only (the
   *  backend rejects it in bring-your-own-IdP mode). */
  resetPassword(staffId: string, signal?: AbortSignal): Promise<{ email?: string }> {
    return this.client.unary<{ email?: string }>(SERVICE, 'ResetStaffPassword', { staff_id: staffId }, { signal });
  }

  /** Replace the languages a member can handle (needs staff.manage; rank-bounded,
   *  but a member may always edit their own). The backend sanitizes the codes. */
  setLanguages(staffId: string, languages: string[], signal?: AbortSignal): Promise<WireStaffMember> {
    return this.client.unary<WireStaffMember>(SERVICE, 'SetStaffLanguages', { staff_id: staffId, languages }, { signal });
  }
}

export const staffApiService = new StaffApiService();
